import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  ChevronUp, 
  ChevronDown, 
  User, 
  Route, 
  Globe, 
  Database,
  Activity,
  X,
  Copy,
  Check,
  RefreshCw,
  Trash2
} from 'lucide-react';

/**
 * DevToolsPanel - A collapsible debug panel for development
 * Shows auth state, current route, API calls, and environment info
 */

// API call interceptor storage
const apiCallsRef = { current: [] };
const MAX_API_CALLS = 50;

// Intercept fetch to log API calls
const originalFetch = window.fetch;
window.fetch = async (...args) => {
  const [url, options = {}] = args;
  const startTime = Date.now();
  
  try {
    const response = await originalFetch(...args);
    const duration = Date.now() - startTime;
    
    // Only log API calls (not static assets)
    if (typeof url === 'string' && url.includes('/api/')) {
      const call = {
        id: Date.now(),
        method: options.method || 'GET',
        url: url.replace(window.location.origin, ''),
        status: response.status,
        duration,
        timestamp: new Date().toISOString(),
        ok: response.ok
      };
      apiCallsRef.current = [call, ...apiCallsRef.current].slice(0, MAX_API_CALLS);
      window.dispatchEvent(new CustomEvent('api-call-logged'));
    }
    
    return response;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (typeof url === 'string' && url.includes('/api/')) {
      const call = {
        id: Date.now(),
        method: options.method || 'GET',
        url: url.replace(window.location.origin, ''),
        status: 'ERR',
        duration,
        timestamp: new Date().toISOString(),
        ok: false,
        error: error.message
      };
      apiCallsRef.current = [call, ...apiCallsRef.current].slice(0, MAX_API_CALLS);
      window.dispatchEvent(new CustomEvent('api-call-logged'));
    }
    throw error;
  }
};

const DevToolsPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('auth');
  const [apiCalls, setApiCalls] = useState([]);
  const [copied, setCopied] = useState(null);
  const location = useLocation();
  const { user, loading } = useAuth();
  const panelRef = useRef(null);

  // Listen for API calls
  useEffect(() => {
    const handleApiCall = () => {
      setApiCalls([...apiCallsRef.current]);
    };
    window.addEventListener('api-call-logged', handleApiCall);
    return () => window.removeEventListener('api-call-logged', handleApiCall);
  }, []);

  // Copy to clipboard helper
  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  // Clear API calls
  const clearApiCalls = () => {
    apiCallsRef.current = [];
    setApiCalls([]);
  };

  // Get status color
  const getStatusColor = (status, ok) => {
    if (status === 'ERR') return 'text-red-400';
    if (ok) return 'text-green-400';
    if (status >= 400) return 'text-red-400';
    if (status >= 300) return 'text-yellow-400';
    return 'text-green-400';
  };

  // Get method color
  const getMethodColor = (method) => {
    const colors = {
      GET: 'bg-blue-500/20 text-blue-400',
      POST: 'bg-green-500/20 text-green-400',
      PUT: 'bg-yellow-500/20 text-yellow-400',
      PATCH: 'bg-orange-500/20 text-orange-400',
      DELETE: 'bg-red-500/20 text-red-400'
    };
    return colors[method] || 'bg-gray-500/20 text-gray-400';
  };

  const tabs = [
    { id: 'auth', label: 'Auth', icon: User },
    { id: 'route', label: 'Route', icon: Route },
    { id: 'api', label: 'API Calls', icon: Activity, badge: apiCalls.length },
    { id: 'env', label: 'Environment', icon: Globe },
  ];

  return (
    <>
      {/* Toggle Button - Always visible */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 z-[9999] bg-[#1a1a2e] hover:bg-[#252542] text-white px-3 py-2 rounded-lg shadow-lg border border-white/10 flex items-center gap-2 text-xs font-mono transition-all"
        data-testid="devtools-toggle"
      >
        <Database className="w-3.5 h-3.5 text-[#F5C056]" />
        <span>DevTools</span>
        {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
        {apiCalls.length > 0 && !isOpen && (
          <span className="bg-[#F5C056] text-black text-[10px] px-1.5 py-0.5 rounded-full font-bold">
            {apiCalls.length}
          </span>
        )}
      </button>

      {/* Panel */}
      {isOpen && (
        <div 
          ref={panelRef}
          className="fixed bottom-0 left-0 right-0 z-[9998] bg-[#0d0d1a] border-t border-white/10 shadow-2xl font-mono text-xs"
          style={{ height: '280px' }}
          data-testid="devtools-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 bg-[#1a1a2e] border-b border-white/10">
            <div className="flex items-center gap-1">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded transition-all ${
                    activeTab === tab.id 
                      ? 'bg-[#F5C056]/20 text-[#F5C056]' 
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <tab.icon className="w-3.5 h-3.5" />
                  <span>{tab.label}</span>
                  {tab.badge > 0 && (
                    <span className="bg-[#F5C056]/30 text-[#F5C056] text-[10px] px-1.5 py-0.5 rounded-full">
                      {tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-white p-1 rounded hover:bg-white/10 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 overflow-auto" style={{ height: 'calc(100% - 45px)' }}>
            
            {/* Auth Tab */}
            {activeTab === 'auth' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-2 h-2 rounded-full ${user ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="text-gray-300">
                    {loading ? 'Loading...' : user ? 'Authenticated' : 'Not Authenticated'}
                  </span>
                </div>
                
                {user ? (
                  <div className="bg-[#1a1a2e] rounded-lg p-4 border border-white/5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">User ID</p>
                        <div className="flex items-center gap-2">
                          <code className="text-green-400">{user.id?.slice(0, 12)}...</code>
                          <button 
                            onClick={() => copyToClipboard(user.id, 'user-id')}
                            className="text-gray-500 hover:text-white"
                          >
                            {copied === 'user-id' ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Email</p>
                        <code className="text-blue-400">{user.email}</code>
                      </div>
                      <div>
                        <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Name</p>
                        <code className="text-white">{user.name || '—'}</code>
                      </div>
                      <div>
                        <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Roles</p>
                        <div className="flex gap-1">
                          {(user.roles || ['user']).map(role => (
                            <span 
                              key={role}
                              className={`px-2 py-0.5 rounded text-[10px] ${
                                role === 'admin' 
                                  ? 'bg-red-500/20 text-red-400' 
                                  : 'bg-blue-500/20 text-blue-400'
                              }`}
                            >
                              {role}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    {user._impersonated_by && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <p className="text-yellow-400 text-[10px]">⚠️ Impersonated by admin: {user._impersonated_by}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-[#1a1a2e] rounded-lg p-4 border border-white/5 text-gray-500">
                    No user session. Login to see auth details.
                  </div>
                )}

                <div className="bg-[#1a1a2e] rounded-lg p-4 border border-white/5">
                  <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-2">Token Status</p>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Access:</span>
                      <span className={localStorage.getItem('access_token') ? 'text-green-400' : 'text-red-400'}>
                        {localStorage.getItem('access_token') ? 'Present' : 'Missing'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Refresh:</span>
                      <span className={localStorage.getItem('refresh_token') ? 'text-green-400' : 'text-red-400'}>
                        {localStorage.getItem('refresh_token') ? 'Present' : 'Missing'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Route Tab */}
            {activeTab === 'route' && (
              <div className="space-y-3">
                {/* Current Preview URL - Prominent */}
                <div className="bg-gradient-to-r from-[#F5C056]/10 to-[#1a1a2e] rounded-lg p-4 border border-[#F5C056]/20">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[#F5C056] text-[10px] uppercase tracking-wider font-bold">Current Preview URL</p>
                    <button 
                      onClick={() => copyToClipboard(window.location.href, 'preview-url')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                        copied === 'preview-url' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-[#F5C056]/20 text-[#F5C056] hover:bg-[#F5C056]/30'
                      }`}
                    >
                      {copied === 'preview-url' ? (
                        <>
                          <Check className="w-3 h-3" />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          <span>Copy URL</span>
                        </>
                      )}
                    </button>
                  </div>
                  <code className="text-white text-sm break-all block bg-black/30 rounded px-3 py-2">
                    {window.location.href}
                  </code>
                </div>

                {/* Route Details */}
                <div className="bg-[#1a1a2e] rounded-lg p-4 border border-white/5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Pathname</p>
                      <code className="text-[#F5C056]">{location.pathname}</code>
                    </div>
                    <div>
                      <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Search</p>
                      <code className="text-blue-400">{location.search || '(none)'}</code>
                    </div>
                    <div>
                      <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Hash</p>
                      <code className="text-green-400">{location.hash || '(none)'}</code>
                    </div>
                    <div>
                      <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">State</p>
                      <code className="text-purple-400">
                        {location.state ? JSON.stringify(location.state).slice(0, 30) : '(none)'}
                      </code>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* API Calls Tab */}
            {activeTab === 'api' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-400">{apiCalls.length} calls logged</span>
                  <button 
                    onClick={clearApiCalls}
                    className="flex items-center gap-1 text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    <span>Clear</span>
                  </button>
                </div>
                
                {apiCalls.length === 0 ? (
                  <div className="bg-[#1a1a2e] rounded-lg p-4 border border-white/5 text-gray-500 text-center">
                    No API calls logged yet. Interact with the app to see requests.
                  </div>
                ) : (
                  <div className="space-y-1 max-h-[180px] overflow-y-auto">
                    {apiCalls.map(call => (
                      <div 
                        key={call.id}
                        className="bg-[#1a1a2e] rounded px-3 py-2 border border-white/5 flex items-center gap-3 hover:bg-[#252542] transition-colors"
                      >
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${getMethodColor(call.method)}`}>
                          {call.method}
                        </span>
                        <span className={`font-bold ${getStatusColor(call.status, call.ok)}`}>
                          {call.status}
                        </span>
                        <span className="text-gray-300 flex-1 truncate">{call.url}</span>
                        <span className="text-gray-500">{call.duration}ms</span>
                        <span className="text-gray-600 text-[10px]">
                          {new Date(call.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Environment Tab */}
            {activeTab === 'env' && (
              <div className="space-y-3">
                <div className="bg-[#1a1a2e] rounded-lg p-4 border border-white/5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">API URL</p>
                      <code className="text-green-400 break-all text-[11px]">
                        {process.env.REACT_APP_BACKEND_URL || '(not set)'}
                      </code>
                    </div>
                    <div>
                      <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Node Env</p>
                      <code className="text-blue-400">{process.env.NODE_ENV}</code>
                    </div>
                    <div>
                      <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Origin</p>
                      <code className="text-purple-400 break-all text-[11px]">{window.location.origin}</code>
                    </div>
                    <div>
                      <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">User Agent</p>
                      <code className="text-gray-400 text-[10px] truncate block">
                        {navigator.userAgent.slice(0, 50)}...
                      </code>
                    </div>
                  </div>
                </div>

                <div className="bg-[#1a1a2e] rounded-lg p-4 border border-white/5">
                  <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-2">Build Info</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <span className="text-gray-400">React:</span>
                      <span className="text-white ml-2">{React.version}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Screen:</span>
                      <span className="text-white ml-2">{window.innerWidth}x{window.innerHeight}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Online:</span>
                      <span className={`ml-2 ${navigator.onLine ? 'text-green-400' : 'text-red-400'}`}>
                        {navigator.onLine ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DevToolsPanel;
